import { Typography, TypographyVariants } from '../Typography';
import { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { Loading } from '../svgs';

export interface ButtonProps extends ComponentProps<'button'> {
  labelClassName?: string;
  labelVariant?: TypographyVariants;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'medium' | 'small';
  color?: 'white' | 'main' | 'blue' | 'red' | 'gold' | 'purple';
  autoPadding?: boolean;
  endIcon?: ReactNode;
  loading?: boolean;
}

const ButtonCVA = cva('py-[--pxv-12] px-[--pxv-24] shadow-button ', {
  variants: {
    autoPadding: {
      true: '',
      false: 'p-0',
    },
    variant: {
      outlined: 'rounded-sm border bg-[transparent] focus:border-4',
      contained: 'rounded-sm',
      text: '',
    },
    size: {
      medium: 'h-[--button-medium-height] ',
      small: 'h-[--button-small-height]',
    },
    color: {
      main: '',
      blue: '',
      red: '',
      '??': '',
      white: '',
      gold: 'bg-[--color-gold-100]',
      purple: 'bg-[--color-gold-100]',
    },
    disabled: {
      true: '!bg-[--button-common-disabled-bg] !text-[--button-common-disabled-text]',
      false: '',
    },
    loading: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'text',
      color: 'white',
      loading: false,
      className: 'text-[--button-text-white-base-text]',
    },
    {
      variant: 'text',
      color: 'white',
      loading: true,
      className: '',
    },
    {
      variant: 'contained',
      color: 'blue',
      loading: false,
      className: [
        'text-[--button-contained-blue-base-text]',
        'bg-[--button-contained-blue-base-bg]',
        'hover:bg-[--button-contained-blue-hover-bg]',
        'focus:bg-[--button-contained-blue-focus-bg]',
        'focus:shadow-[--button-contained-blue-focus-shadow]',
      ],
    },
    {
      variant: 'contained',
      color: 'blue',
      loading: true,
      className:
        'text-[--button-contained-blue-loading-text] bg-[--button-contained-blue-loading-bg]',
    },

    {
      variant: 'outlined',
      color: 'blue',
      loading: false,
      className: [
        'text-[--button-outlined-blue-base-text]',
        'border-[--button-outlined-blue-base-text]',
        'hover:text-[--button-outlined-blue-hover-text]',
        'hover:border-[--button-outlined-blue-hover-text]',
        'focus:border-[--button-outlined-blue-focus-border]',
      ],
    },
    {
      variant: 'outlined',
      color: 'blue',
      loading: true,
      className: 'text-[--button-contained-blue-loading-text]',
    },
    {
      variant: 'outlined',
      color: 'white',
      loading: false,
      className: [
        'text-[--color-neutral-0]',
        'border-[--color-neutral-0]',
        'hover:text-[--color-opacity-light]',
        'hover:border-[--color-opacity-light]',
      ],
    },
    {
      variant: 'outlined',
      color: 'main',
      loading: false,
      className: [
        'text-[--color-neutral-900]',
        'border-[--color-neutral-900]',
        'hover:text-[--color-neutral-700]',
        'hover:border-[--color-neutral-700]',
      ],
    },
    // ? TBC
    {
      variant: 'contained',
      color: 'main',
      disabled: true,
      className: '',
    },

    {
      variant: 'contained',
      color: 'red',
      disabled: true,
      className: '',
    },
    {
      variant: 'contained',
      color: '??',
      disabled: true,
      className: '',
    },
    {
      variant: 'contained',
      color: 'gold',
      disabled: true,
      className: '',
    },
    {
      variant: 'contained',
      color: 'purple',
      disabled: true,
      className: '',
    },
  ],
  defaultVariants: {
    size: 'medium',
    variant: 'contained',
    color: 'main',
    disabled: false,
    loading: false,
  },
});

export const Button = ({
  children,
  className,
  disabled = false,
  color,
  variant,
  loading = false,
  labelClassName,
  labelVariant = 'button020',
  endIcon,
  autoPadding,
  size,
  ...otherProps
}: PropsWithChildren<ButtonProps>) => {
  return (
    <button
      className={twMerge(
        ButtonCVA({ size, variant, color, loading, disabled, autoPadding }),
        'relative items-center flex-0 flex gap-[--padding-xs]',
        className
      )}
      disabled={disabled || loading}
      {...otherProps}
    >
      <Typography
        as="label"
        className={twMerge(
          ['text-center', 'pointer-events-none', loading ? 'opacity-0' : 'opacity-100', 'w-full'],
          labelClassName
        )}
        variant={labelVariant}
      >
        {children}
      </Typography>
      {endIcon}
      <div
        className={twMerge([
          'absolute inset-0 flex items-center justify-center pointer-events-none',
          loading ? 'opacity-100' : 'opacity-0',
        ])}
      >
        <Loading height={24} width={24} className="animate-spin" />
      </div>
    </button>
  );
};
